import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Plus,
  Edit3,
  Trash2,
  ExternalLink,
  Calendar,
  Tag,
  Rocket,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import AdminPageShell from "../components/AdminPageShell";
import { API } from "../apiBase";

/**
 * AdminBlog — `/admin/blog`
 * Lists all blog posts (drafts + published) with edit / delete / IndexNow actions.
 * Uses the shared AdminPageShell for the unified header + tabs nav.
 */
export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pushingPostId, setPushingPostId] = useState(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/blog/posts`);
      setPosts(data);
    } catch (e) {
      setErr("Impossible de charger les articles.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement « ${title} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/posts/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
      toast.success(`« ${title} » supprimé.`);
    } catch (e) {
      toast.error("Suppression impossible.");
    }
  };

  const onPushPost = async (postId, title) => {
    setPushingPostId(postId);
    try {
      const { data } = await axios.post(`${API}/admin/blog/posts/${postId}/indexnow-push`);
      if (data?.status === "ok") {
        toast.success(`« ${title} » re-poussé à IndexNow`);
      } else {
        toast.warning(`Statut : ${data?.code || data?.error || "inconnu"}`);
      }
    } catch (e) {
      const reason = e?.response?.data?.detail || "Échec";
      toast.error(reason);
    } finally {
      setPushingPostId(null);
    }
  };

  return (
    <AdminPageShell
      title="Articles du blog"
      subtitle="Gérez votre contenu SEO — création, édition et indexation IndexNow"
      tab="blog"
      seoTitle="Admin — Articles du blog MetaConnect"
      seoCanonical="/admin/blog"
    >
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="text-[12.5px] text-[var(--mc-muted)]">
          {loading ? "Chargement…" : `${posts.length} article${posts.length > 1 ? "s" : ""} au total`}
        </div>
        <Link
          to="/admin/new"
          data-testid="admin-blog-new"
          className="btn-primary px-5 py-2.5 rounded-full text-[13px] font-medium inline-flex items-center gap-2"
        >
          <Plus size={14} /> Nouvel article
        </Link>
      </div>

      {err && (
        <div className="mc-card p-4 mb-4 border-[rgba(255,120,120,0.4)] bg-[rgba(255,120,120,0.06)]">
          <p className="text-[13px] text-[#DC2626]">{err}</p>
        </div>
      )}

      {!loading && posts.length === 0 && (
        <div className="mc-card p-12 text-center" data-testid="admin-blog-empty">
          <h3 className="font-display text-[#0F2B5B] text-xl">Aucun article pour le moment</h3>
          <p className="mt-2 text-[var(--mc-muted)] text-[14px]">
            Créez votre premier article pour booster le SEO du site.
          </p>
          <Link
            to="/admin/new"
            className="btn-primary px-6 py-3 rounded-full text-[13px] font-medium inline-flex items-center gap-2 mt-6"
          >
            <Plus size={16} /> Rédiger un article
          </Link>
        </div>
      )}

      <div className="grid gap-3">
        {posts.map((p) => (
          <article
            key={p.id}
            data-testid={`admin-post-${p.id}`}
            className="mc-card p-5 flex flex-wrap items-center gap-4 justify-between"
          >
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  className={`text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border ${
                    p.status === "published"
                      ? "border-[rgba(110,231,197,0.4)] bg-[rgba(110,231,197,0.08)] text-[var(--mc-mint)]"
                      : "border-[rgba(127,179,255,0.3)] bg-[rgba(127,179,255,0.08)] text-[#0284C7]"
                  }`}
                >
                  {p.status === "published" ? "Publié" : "Brouillon"}
                </span>
                {(p.tags || []).slice(0, 3).map((t) => (
                  <span
                    key={t}
                    className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full border border-[rgba(127,179,255,0.3)] text-[var(--mc-muted)] inline-flex items-center gap-1"
                  >
                    <Tag size={10} /> {t}
                  </span>
                ))}
              </div>
              <h3 className="font-display text-[#0F2B5B] text-[18px] mt-2">{p.title}</h3>
              <div className="text-[12px] text-[var(--mc-muted)] mt-1.5 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} />
                  {new Date(p.updated_at).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span>· /blog/{p.slug}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {p.status === "published" && (
                <Link
                  to={`/blog/${p.slug}`}
                  target="_blank"
                  className="btn-ghost px-3 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5"
                >
                  <ExternalLink size={14} /> Voir
                </Link>
              )}
              {p.status === "published" && (
                <button
                  onClick={() => onPushPost(p.id, p.title)}
                  disabled={pushingPostId === p.id}
                  data-testid={`admin-indexnow-${p.id}`}
                  title="Re-pousser cet article aux moteurs de recherche (IndexNow)"
                  className="btn-ghost px-3 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5 disabled:opacity-60 disabled:cursor-wait"
                >
                  {pushingPostId === p.id ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Rocket size={14} />
                  )}
                  {pushingPostId === p.id ? "Envoi…" : "Re-pousser"}
                </button>
              )}
              <Link
                to={`/admin/edit/${p.id}`}
                data-testid={`admin-edit-${p.id}`}
                className="btn-ghost px-3 py-2 rounded-full text-[12px] inline-flex items-center gap-1.5"
              >
                <Edit3 size={14} /> Éditer
              </Link>
              <button
                onClick={() => onDelete(p.id, p.title)}
                data-testid={`admin-delete-${p.id}`}
                className="px-3 py-2 rounded-full text-[12px] border border-[rgba(255,120,120,0.3)] bg-[rgba(255,120,120,0.06)] text-[#DC2626] hover:bg-[rgba(255,120,120,0.12)] inline-flex items-center gap-1.5 transition"
              >
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
          </article>
        ))}
      </div>
    </AdminPageShell>
  );
}
